import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Eye, EyeOff, Mail, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  // Invite gate state
  const [checking, setChecking] = useState(true);
  const [gateError, setGateError] = useState(""); // empty = token valid
  const [inviteEmail, setInviteEmail] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Validate the invite token on mount + pre-fill fields from it
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setGateError("missing");
      setChecking(false);
      return () => {};
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (data?.business_name) setBusinessName(data.business_name);
        if (data?.contact_email) {
          setInviteEmail(data.contact_email);
          setEmail(data.contact_email);
        }
        setGateError("");
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 410) setGateError("used_or_expired");
        else setGateError("invalid");
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !businessName || !email || !password || !passwordConfirm) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/signup", {
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        business_name: businessName,
        contact_email: email,
        password,
        origin_url: window.location.origin,
        invite_token: token,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout.");
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Signup failed.";
      toast.error(String(msg));
      setSubmitting(false);
    }
    // Silence unused-var lint for navigate (kept for future post-checkout flows)
    void navigate;
  };

  return (
    <div className="relative min-h-[80vh] bg-[#0A1628] text-[#F8FAFC] py-20">
      <div className="absolute inset-0 so-grid opacity-25 pointer-events-none" />
      <div className="absolute top-20 right-0 w-[480px] h-[480px] so-glow-cyan pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[480px] h-[480px] so-glow-blue pointer-events-none" />

      <div className="relative max-w-xl mx-auto px-6">
        {checking && (
          <div data-testid="signup-checking" className="so-card rounded-2xl p-10 text-center">
            <Loader2 className="animate-spin mx-auto text-[#06B6D4]" size={28} />
            <p className="mt-4 text-[#94A3B8] text-sm">Validating your invite…</p>
          </div>
        )}

        {!checking && gateError && (
          <div
            data-testid="signup-invite-gate"
            className="so-card rounded-2xl p-10 text-center max-w-md mx-auto"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 mb-4">
              <Mail className="text-[#06B6D4]" size={22} />
            </div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">
              This page is by invitation only
            </h1>
            <p className="mt-4 text-[#94A3B8] text-sm leading-relaxed">
              {gateError === "used_or_expired"
                ? "This invite link has already been used or has expired."
                : null}
              {gateError === "used_or_expired" && <br />}
              If you have received an invitation from GenQuotes please check your
              email for your personal signup link.
            </p>
            <Link
              to="/get-started"
              data-testid="signup-gate-getstarted-link"
              className="mt-6 btn-pink inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
            >
              New here? Get started <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {!checking && !gateError && (
          <>
            <div className="text-center mb-8">
              <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">
                Create your account
              </div>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
                Start your free 28-day trial
              </h1>
              <p className="mt-4 text-[#94A3B8] leading-relaxed text-sm max-w-md mx-auto">
                Your widget is ready. Create your account below to start your free
                28-day trial — <strong className="text-[#F8FAFC]">$0 today</strong>, first charge on day 28, cancel anytime.
              </p>
              {inviteEmail && (
                <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono text-[#06B6D4] bg-[#06B6D4]/5 border border-[#06B6D4]/20 rounded-full px-3 py-1">
                  <AlertCircle size={12} /> Invite verified for {inviteEmail}
                </div>
              )}
            </div>

        <form
          onSubmit={onSubmit}
          data-testid="signup-form"
          className="so-card rounded-2xl p-6 sm:p-8"
        >
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                  First name
                </label>
                <input
                  data-testid="signup-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                  className="so-input w-full rounded-lg px-4 py-3 text-sm"
                  autoComplete="given-name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                  Last name
                </label>
                <input
                  data-testid="signup-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="so-input w-full rounded-lg px-4 py-3 text-sm"
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                Business name
              </label>
              <input
                data-testid="signup-business-name"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g. SparkleClean Co."
                className="so-input w-full rounded-lg px-4 py-3 text-sm"
                autoComplete="organization"
                required
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                Email address
              </label>
              <input
                data-testid="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                className="so-input w-full rounded-lg px-4 py-3 text-sm"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                Password
              </label>
              <div className="relative">
                <input
                  data-testid="signup-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="so-input w-full rounded-lg px-4 py-3 text-sm pr-10"
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#F8FAFC]"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                Confirm password
              </label>
              <input
                data-testid="signup-password-confirm"
                type={showPw ? "text" : "password"}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Re-enter your password"
                className="so-input w-full rounded-lg px-4 py-3 text-sm"
                autoComplete="new-password"
                minLength={8}
                required
              />
              {passwordConfirm && password !== passwordConfirm && (
                <div
                  data-testid="signup-password-mismatch"
                  className="mt-2 text-xs text-[#EC4899]"
                >
                  Passwords don&apos;t match yet.
                </div>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="signup-submit-btn"
                className="btn-pink w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Creating your account…
                  </>
                ) : (
                  <>
                    Start free trial <ArrowRight size={16} />
                  </>
                )}
              </button>
              <div className="text-[11px] text-[#94A3B8] mt-3 text-center font-mono">
                Next step: enter your card on Stripe · No charge for 28 days · Cancel anytime
              </div>
            </div>

            <div className="text-xs text-[#94A3B8] text-center">
              Already have an account?{" "}
              <Link to="/account" className="text-[#06B6D4] hover:underline">
                Sign in
              </Link>
            </div>
          </div>
        </form>
          </>
        )}
      </div>
    </div>
  );
}
